import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X, GripVertical, Image as ImageIcon, Eye, EyeOff } from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";

const blank = {
  slug: "", label: "", cta: "Explore", theme: "",
  headline: "", sub: "",
  accent: "#FFC93C",
  bg: "linear-gradient(180deg, #FFF8F0 0%, #FFF1D6 100%)",
  dark: false,
  hero: "",
  video_url: "",
  vibes: [],
  is_published: true,
  sort_order: 0,
};

export default function AdminWorlds() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/admin/worlds").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing({ ...blank, sort_order: (items.at(-1)?.sort_order ?? 0) + 1 }); setOpen(true); };
  const startEdit = (w) => { setEditing({ ...w, vibes: w.vibes || [] }); setOpen(true); };

  const save = async () => {
    if (!editing.slug || !editing.label) return toast.error("Slug & label required");
    const body = { ...editing };
    delete body.id; delete body.created_at; delete body.updated_at;
    try {
      const exists = items.some((i) => i.slug === editing.slug);
      if (exists) await api.put(`/admin/worlds/${editing.slug}`, body);
      else await api.post(`/admin/worlds`, body);
      toast.success("Saved");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (slug) => {
    if (!window.confirm(`Delete world '${slug}'?`)) return;
    await api.delete(`/admin/worlds/${slug}`);
    toast.success("Deleted"); load();
  };

  const togglePublish = async (w) => {
    const body = { ...w, is_published: !w.is_published }; delete body.id; delete body.created_at; delete body.updated_at;
    await api.put(`/admin/worlds/${w.slug}`, body); load();
  };

  const addVibe = () => setEditing({ ...editing, vibes: [...(editing.vibes || []), { img: "", caption: "" }] });
  const setVibe = (i, k, v) => { const arr = [...editing.vibes]; arr[i] = { ...arr[i], [k]: v }; setEditing({ ...editing, vibes: arr }); };
  const delVibe = (i) => setEditing({ ...editing, vibes: editing.vibes.filter((_, idx) => idx !== i) });
  const moveVibe = (i, dir) => {
    const arr = [...editing.vibes]; const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setEditing({ ...editing, vibes: arr });
  };

  return (
    <div data-testid="admin-worlds" className="space-y-8">
      <header className="flex items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Category Worlds (CMS)</div>
          <h1 className="editorial-h1 text-5xl mt-2">{items.length} worlds editable.</h1>
          <p className="text-sm text-brand-charcoal/65 mt-2">Change headlines, banners, vibes, themes — no developer required.</p>
        </div>
        <button data-testid="world-new" onClick={startNew} className="btn-pill btn-dark text-sm"><Plus size={16} /> New World</button>
      </header>

      <div className="grid md:grid-cols-2 gap-5">
        {items.map((w) => (
          <div key={w.slug} className="card-elevated overflow-hidden" data-testid={`world-card-${w.slug}`}>
            <div className="relative aspect-[16/9] overflow-hidden">
              {w.hero ? <img src={w.hero} alt={w.label} className="h-full w-full object-cover" /> : <div className="h-full w-full grid place-items-center bg-brand-muted text-brand-charcoal/50"><ImageIcon /></div>}
              <div className={`absolute inset-0 ${w.dark ? "bg-brand-charcoal/40" : "bg-brand-cream/30"}`} />
              <div className={`absolute bottom-0 left-0 right-0 p-5 ${w.dark ? "text-brand-cream" : "text-brand-charcoal"}`}>
                <div className="text-[10px] uppercase tracking-[0.22em] opacity-75">{w.theme}</div>
                <div className="font-display text-2xl">{w.label}</div>
                <div className="text-sm italic">{w.headline}</div>
              </div>
              {!w.is_published && <div className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.18em] bg-brand-red text-white px-2 py-0.5 rounded-full">Draft</div>}
            </div>
            <div className="p-4 flex items-center justify-between gap-2">
              <div className="text-xs text-brand-charcoal/65">/{w.slug} · {w.vibes?.length || 0} vibes</div>
              <div className="flex gap-2">
                <button onClick={() => togglePublish(w)} title={w.is_published ? "Unpublish" : "Publish"} className="btn-pill btn-ghost text-xs border border-brand-line">{w.is_published ? <Eye size={14}/> : <EyeOff size={14} />}</button>
                <button data-testid={`world-edit-${w.slug}`} onClick={() => startEdit(w)} className="btn-pill btn-ghost text-xs border border-brand-line">Edit</button>
                <button onClick={() => del(w.slug)} className="btn-pill btn-ghost text-xs"><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {open && editing && (
        <div className="fixed inset-0 z-50 bg-brand-charcoal/40 grid place-items-center p-4" data-testid="world-modal">
          <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-8">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-3xl">{items.some((i) => i.slug === editing.slug) ? `Edit "${editing.label || editing.slug}"` : "New world"}</div>
              <button onClick={() => setOpen(false)}><X /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Slug (url)</label><input data-testid="w-slug" className="pp-input" disabled={items.some((i)=>i.slug===editing.slug)} value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })} /></div>
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Sort order</label><input data-testid="w-order" type="number" className="pp-input" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: Number(e.target.value) || 0 })} /></div>
              <div className="sm:col-span-2"><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Title / Label</label><input data-testid="w-label" className="pp-input" value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} /></div>
              <div className="sm:col-span-2"><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Headline (large editorial)</label><input data-testid="w-headline" className="pp-input font-display text-2xl" value={editing.headline} onChange={(e) => setEditing({ ...editing, headline: e.target.value })} /></div>
              <div className="sm:col-span-2"><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Sub headline</label><textarea data-testid="w-sub" rows="2" className="pp-input" value={editing.sub} onChange={(e) => setEditing({ ...editing, sub: e.target.value })} /></div>
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">CTA button text</label><input data-testid="w-cta" className="pp-input" value={editing.cta} onChange={(e) => setEditing({ ...editing, cta: e.target.value })} /></div>
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Theme tagline</label><input data-testid="w-theme" className="pp-input" placeholder="Summer · Childhood · Outdoors" value={editing.theme} onChange={(e) => setEditing({ ...editing, theme: e.target.value })} /></div>
              <div className="sm:col-span-2"><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Hero image URL</label><input data-testid="w-hero" className="pp-input" value={editing.hero} onChange={(e) => setEditing({ ...editing, hero: e.target.value })} /></div>
              <div className="sm:col-span-2"><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Hero video URL (optional, overrides image)</label><input data-testid="w-video" className="pp-input" placeholder="https://...mp4" value={editing.video_url || ""} onChange={(e) => setEditing({ ...editing, video_url: e.target.value })} /></div>
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Accent</label><input data-testid="w-accent" type="color" className="h-10 w-16 border border-brand-line rounded-md" value={editing.accent} onChange={(e) => setEditing({ ...editing, accent: e.target.value })} /></div>
              <label className="flex items-center gap-2 text-sm self-end pb-2"><input type="checkbox" data-testid="w-dark" checked={!!editing.dark} onChange={(e) => setEditing({ ...editing, dark: e.target.checked })} /> Dark theme</label>
              <div className="sm:col-span-2"><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Background gradient (CSS)</label><input data-testid="w-bg" className="pp-input font-mono text-xs" value={editing.bg} onChange={(e) => setEditing({ ...editing, bg: e.target.value })} /></div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="w-pub" checked={!!editing.is_published} onChange={(e) => setEditing({ ...editing, is_published: e.target.checked })} /> Published</label>
            </div>

            {/* Vibes manager */}
            <div className="mt-8">
              <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Vibe sections</div>
                <button data-testid="w-vibe-add" onClick={addVibe} className="btn-pill btn-ghost text-xs border border-brand-line"><Plus size={14} /> Add vibe</button>
              </div>
              <div className="mt-3 space-y-3">
                {editing.vibes.map((v, i) => (
                  <div key={i} className="card-soft p-3 flex flex-col sm:flex-row gap-3 items-start" data-testid={`w-vibe-${i}`}>
                    <div className="flex flex-col gap-1">
                      <button onClick={() => moveVibe(i, -1)} className="text-xs p-1 border border-brand-line rounded">↑</button>
                      <button onClick={() => moveVibe(i, +1)} className="text-xs p-1 border border-brand-line rounded">↓</button>
                    </div>
                    {v.img && <img src={v.img} alt="" className="h-16 w-20 object-cover rounded-lg" />}
                    <div className="flex-1 grid gap-2">
                      <input className="pp-input" placeholder="Image URL" value={v.img} onChange={(e) => setVibe(i, "img", e.target.value)} data-testid={`w-vibe-img-${i}`} />
                      <input className="pp-input" placeholder="Caption" value={v.caption} onChange={(e) => setVibe(i, "caption", e.target.value)} data-testid={`w-vibe-cap-${i}`} />
                    </div>
                    <button onClick={() => delVibe(i)} className="text-brand-red"><Trash2 size={14} /></button>
                  </div>
                ))}
                {editing.vibes.length === 0 && <div className="text-xs text-brand-charcoal/55">No vibe sections yet — add scenic imagery + captions to bring this world to life.</div>}
              </div>
            </div>

            <div className="mt-8 flex justify-end gap-3">
              <button onClick={() => setOpen(false)} className="btn-pill btn-ghost text-sm">Cancel</button>
              <button data-testid="w-save" onClick={save} className="btn-pill btn-dark text-sm">Save World</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
